import { axios_adapter } from '../network/axios_adapter';
import ENDPOINTS from '../network/endpoints';
import { fetchUserProfile } from './authService';
import { getUserProfile } from '../storage/userProfileStorage';

const GAME_STAT_DEFAULTS = [
  { id: 'raphousie', label: 'RAPHOUSIE', tone: '#009dc9' },
  { id: 'rapwheel', label: 'RAPWHEEL', tone: '#ff00f2' },
  { id: 'rapfire', label: 'RAPFIRE', tone: '#ff8900' },
  { id: 'rapleague', label: 'RAPLEAGUE', tone: '#02ca0c' },
];

function pickString(source, keys, fallback = '') {
  for (const key of keys) {
    const value = source?.[key];
    if (value != null && String(value).trim()) return String(value).trim();
  }
  return fallback;
}

function pickNumber(source, keys, fallback = 0) {
  for (const key of keys) {
    const value = source?.[key];
    if (value != null && value !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return fallback;
}

function unwrapProfilePayload(profile) {
  if (!profile || typeof profile !== 'object') return null;
  if (
    profile.data &&
    typeof profile.data === 'object' &&
    !pickString(profile, ['userName', 'userId', 'phoneNumber', 'cognitoId'])
  ) {
    return profile.data;
  }
  return profile;
}

function getRapCardSection(profile) {
  if (!profile || typeof profile !== 'object') return {};
  return profile.rapCard || profile.rapcard || profile.rapCardStats || profile.stats || {};
}

function normalizeRapcardPayload(payload) {
  const root = unwrapProfilePayload(payload) || payload;
  if (!root || typeof root !== 'object') return null;

  const user = root.user && typeof root.user === 'object' && !Array.isArray(root.user) ? root.user : null;
  const game = Array.isArray(root.game)
    ? root.game
    : Array.isArray(root.games)
      ? root.games
      : Array.isArray(user?.game)
        ? user.game
        : [];

  if (user) {
    return { ...user, game };
  }

  return { ...root, game };
}

function formatProfileDate(value, fallback = '—') {
  if (value == null || value === '') return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatPassEndLabel(value) {
  if (value == null || value === '') return 'End on : —';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return `End on : ${formatProfileDate(value)}`;
  return `End on : ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function formatSubscribedLabel(value) {
  if (value == null || value === '') return 'Subscribed on : —';
  return `Subscribed on : ${formatProfileDate(value)}`;
}

function formatPhone(profile) {
  const phone = pickString(profile, ['phone', 'mobile', 'mobileNumber', 'phoneNumber']);
  const code = pickString(profile, ['countryCode', 'dialCode'], '+91');
  if (!phone) return '—';
  if (phone.startsWith('+')) return phone;
  return `${code} ${phone}`;
}

function matchGameId(entry, fallbackId) {
  const raw = pickString(entry, ['id', 'game', 'gameType', 'gameName', 'name'], fallbackId).toLowerCase();
  if (raw.includes('housie') || raw.includes('raphousie')) return 'raphousie';
  if (raw.includes('wheel') || raw.includes('rapwheel')) return 'rapwheel';
  if (raw.includes('fire') || raw.includes('rapfire')) return 'rapfire';
  if (raw.includes('league') || raw.includes('rapleague')) return 'rapleague';
  return fallbackId;
}

function normalizeGameStat(entry, fallback) {
  const base = { ...fallback, played: 0, win: 0, lose: 0 };
  if (!entry || typeof entry !== 'object') return base;

  return {
    id: matchGameId(entry, fallback.id),
    label: pickString(entry, ['label', 'game', 'gameName', 'name'], fallback.label).toUpperCase(),
    played: pickNumber(entry, ['played', 'total', 'totalPlayed', 'gamesPlayed'], 0),
    win: pickNumber(entry, ['win', 'wins', 'won', 'totalWins'], 0),
    lose: pickNumber(entry, ['lose', 'loss', 'losses', 'lost', 'totalLosses'], 0),
    tone: entry.tone || fallback.tone,
  };
}

function resolveGameStats(profile, rapCard) {
  const source =
    profile?.game ||
    profile?.gameStats ||
    profile?.gamesStats ||
    profile?.games ||
    profile?.contestStats?.games ||
    rapCard?.game ||
    rapCard?.gameStats ||
    rapCard?.gamesStats ||
    rapCard?.games ||
    rapCard?.contestStats?.games;

  if (Array.isArray(source) && source.length) {
    return GAME_STAT_DEFAULTS.map((fallback) => {
      const match =
        source.find((entry) => matchGameId(entry, fallback.id) === fallback.id) ||
        source[GAME_STAT_DEFAULTS.indexOf(fallback)];
      return normalizeGameStat(match, fallback);
    });
  }

  if (source && typeof source === 'object') {
    return GAME_STAT_DEFAULTS.map((fallback) => {
      const match =
        source[fallback.id] ||
        source[fallback.label] ||
        source[fallback.label.toLowerCase()] ||
        Object.entries(source).find(([key, entry]) => matchGameId({ id: key, ...entry }, fallback.id) === fallback.id)?.[1];
      return normalizeGameStat(match, fallback);
    });
  }

  return GAME_STAT_DEFAULTS.map((fallback) => normalizeGameStat(null, fallback));
}

function findActivePass(profile, kind) {
  const lists = [
    profile?.activePasses,
    profile?.passes,
    profile?.subscriptions,
    profile?.activeSubscriptions,
  ].filter(Array.isArray);

  for (const list of lists) {
    const match = list.find((pass) => {
      const type = pickString(pass, ['type', 'passKind', 'packageType', 'iapType'], '').toLowerCase();
      if (kind === 'gem') {
        return type === 'pass' || type.includes('gem');
      }
      return type === 'subscriptionpackage' || type.includes('vip') || type.includes('subscription');
    });
    if (match) return match;
  }

  return null;
}

function mapVipSubscription(profile) {
  if (!profile?.isPremium) {
    return {
      tier: '—',
      status: 'Inactive',
      endDate: formatPassEndLabel(''),
      subscribedOn: formatSubscribedLabel(''),
    };
  }

  const endRaw = profile?.subscriptionExpiresAt ?? profile?.subscriptionEndDate ?? profile?.passExpiresAt;

  return {
    tier: pickString(profile, ['subscriptionDisplayName'], '') || 'Premium',
    status: 'Active',
    endDate: formatPassEndLabel(endRaw),
    subscribedOn: '',
  };
}

function mapPassFields(profile, kind) {
  if (kind === 'vip') {
    return mapVipSubscription(profile);
  }

  const activePass = findActivePass(profile, kind);

  const tier = pickString(
    activePass || profile,
    ['displayName', 'packageName', 'name', 'gemPassTier', 'gemPassName', 'activeGemPass'],
    '—',
  );

  const endRaw = pickString(
    activePass || profile,
    ['expiresAt', 'endDate', 'validTill', 'gemPassEndDate', 'gemPassExpiresAt', 'activeGemPassEndDate'],
    '',
  );

  const startRaw = pickString(activePass || profile, ['startDate', 'subscribedOn', 'gemPassStartDate'], '');

  const isActive =
    activePass?.isActive !== false &&
    (Boolean(activePass) || pickString(profile, ['gemPassStatus'], '') === 'Active');

  return {
    tier,
    status: isActive ? 'Active' : 'Inactive',
    endDate: formatPassEndLabel(endRaw),
    subscribedOn: formatSubscribedLabel(startRaw),
  };
}

/** Premium subscription bar — sidebar / profile (only when isPremium) */
export function getPremiumSubscriptionDisplay(rawProfile = getUserProfile()) {
  const profile = unwrapProfilePayload(rawProfile) || rawProfile || {};
  if (!profile.isPremium) return null;

  const vip = mapVipSubscription(profile);
  return {
    passTier: vip.tier,
    passStatus: vip.status,
    passEndDate: vip.endDate,
  };
}

export function resolveProfileUserId(profile = getUserProfile()) {
  const data = unwrapProfilePayload(profile) || profile || {};
  return pickString(data, ['_id', 'id', 'userId', 'user_id'], '');
}

export async function fetchMyRapcard(userId) {
  const id = userId || resolveProfileUserId(getUserProfile());
  if (!id) {
    throw new Error('Profile id is required to load RapCard.');
  }

  const response = await axios_adapter.get(ENDPOINTS.userMyRapcard(id));
  const body = response.data;

  if (body?.responseCode && body.responseCode !== 200) {
    throw new Error(body?.data?.message || body?.message || 'Unable to load RapCard.');
  }

  return unwrapProfilePayload(body?.data ?? body) || body?.data || body;
}

export function mergeProfileWithRapcard(profile, rapcard) {
  const base = unwrapProfilePayload(profile) || profile || {};
  const card = normalizeRapcardPayload(rapcard);
  if (!card) return base;

  return {
    ...base,
    ...card,
    rapCard: {
      ...getRapCardSection(base),
      ...getRapCardSection(card),
      ...card,
    },
  };
}

export async function fetchMyProfilePageData() {
  const profile = await fetchUserProfile().catch(() => getUserProfile());
  const userId = resolveProfileUserId(profile) || resolveProfileUserId(getUserProfile());
  const rapcard = userId ? await fetchMyRapcard(userId).catch(() => null) : null;
  return mapProfileViewData(mergeProfileWithRapcard(profile, rapcard));
}

export function mapProfileViewData(rawProfile = getUserProfile()) {
  const profile = unwrapProfilePayload(rawProfile) || {};
  const rapCard = getRapCardSection(profile);
  const gameStats = resolveGameStats(profile, rapCard);

  const totalContests = pickNumber(
    profile,
    ['totGame', 'totalContests', 'total_contests', 'contestsPlayed', 'contests_played', 'totalContest', 'contestCount'],
    pickNumber(rapCard, ['totGame', 'totalContests', 'total_contests', 'contestsPlayed', 'contests_played', 'totalContest'], 0),
  );
  const totalWins = pickNumber(
    profile,
    ['totWon', 'totalWins', 'total_wins', 'contestsWon', 'contests_won', 'wins', 'winCount'],
    pickNumber(rapCard, ['totWon', 'totalWins', 'total_wins', 'contestsWon', 'contests_won', 'wins'], 0),
  );
  const totalLosses = pickNumber(
    profile,
    ['lotLoss', 'totLoss', 'totalLosses', 'total_losses', 'contestsLost', 'contests_lost', 'losses', 'loseCount'],
    pickNumber(rapCard, ['lotLoss', 'totLoss', 'totalLosses', 'total_losses', 'contestsLost', 'contests_lost', 'losses'], 0),
  );

  const winningRatioRaw =
    profile?.winningRatio ??
    profile?.winRatio ??
    profile?.winning_ratio ??
    rapCard?.winningRatio ??
    rapCard?.winRatio ??
    rapCard?.winning_ratio;
  const winningRatio =
    winningRatioRaw != null
      ? Number(String(winningRatioRaw).replace('%', ''))
      : totalContests
        ? Number(((totalWins / totalContests) * 100).toFixed(2))
        : 0;

  const vipPass = mapPassFields(profile, 'vip');
  const gemPass = mapPassFields(profile, 'gem');

  return {
    avatarSrc: pickString(profile, ['profilePic', 'profileImage', 'profilePicture', 'avatarUrl', 'avatar', 'imageUrl'], '') || null,
    fullName: pickString(profile, ['fullName', 'name', 'displayName', 'userName', 'username'], '—'),
    displayName: pickString(profile, ['userName', 'username', 'name', 'displayName'], 'Guest'),
    phone: formatPhone(profile),
    email: pickString(profile, ['email', 'emailId', 'emailAddress'], '—'),
    age: pickString(profile, ['ageGroup', 'age'], '—'),
    winningPoints: pickNumber(
      profile,
      ['totalPoints', 'winningPoints', 'totalWinningPoints', 'score', 'rapCoin', 'rapCoinBalance', 'totalCoins', 'rapCoins'],
      pickNumber(rapCard, ['totalPoints', 'winningPoints', 'rapCoin', 'rapCoinBalance', 'totalCoins', 'rapCoins'], 0),
    ),
    earnedXp: pickNumber(
      profile,
      ['totalXP', 'totalXp', 'earnedXp', 'xp'],
      pickNumber(rapCard, ['totalXP', 'totalXp', 'earnedXp', 'xp'], 0),
    ),
    winningRatio: Number.isFinite(winningRatio) ? winningRatio : 0,
    totalContests,
    totalWins,
    totalLosses,
    gameStats,
    passTier: vipPass.tier,
    passStatus: vipPass.status,
    passEndDate: vipPass.endDate,
    subscribedOn: vipPass.subscribedOn,
    gemPassTier: gemPass.tier,
    gemPassStatus: gemPass.status,
    gemPassEndDate: gemPass.endDate,
    referralCode: pickString(profile, ['referralCode', 'code', 'inviteCode'], ''),
    isPremium: Boolean(profile?.isPremium),
  };
}
